<template>
  <div class="view p2p">

    <h1>{{ $t('home.tabs.transportation') }}</h1>

    <h2>{{ $t('p2p.step1') }}</h2>

    <div class="description">{{ model.description }}</div>
      <div class="loading" :class="{show: loading}"></div>
      <div class="form">

        <div class="type-container">
          <form-input type="radio" :label="$t('form.label.p2p.round-trip')" icon="type" class="oneway" name="trip-type" radioValue="roundtrip" v-model="uiState.tripType"/>
          <form-input type="radio" :label="$t('form.label.p2p.one-way')" class="round" name="trip-type" radioValue="oneway" v-model="uiState.tripType"/>
        </div>

        <div class="destination-container">
          <form-input type="select" :label="$t('form.label.p2p.from')" icon="destination" :options="model.originOptions" v-model="uiState.from"/>
          <form-input v-if="model.destinations.length" type="select" :label="$t('form.label.p2p.to')" :options="model.destinationOptions" v-model="uiState.to"/>
        </div>

        <div class="date-container">
          <!-- TO DO Jamie - The form-inputs have same wrapping html/classes as the FormInput component. Missing is the label/placeholder. Would be nice to get that in to the date components -->
          <div class="component form-input input-item text date">
            <span class="icon date"></span>
            <datepicker  class="input" id="p2pDateDeparture" :inline="false" :disabledDates="disabledDates" :value="activeDate" @input="updateActiveDate"></datepicker>
          </div>

          <div class="component form-input input-item text date">
            <datepicker class="input" id="p2pDateReturn" :inline="false" :disabledDates="disabledReturnDates" :value="uiState.returnDate" @input="updateReturnDate" v-if="isRoundTrip"></datepicker>
          </div>
        </div>

        <guest-picker v-if="paxEnabled.length" ref="guestPicker" :paxPrefix="paxPrefix" :pricing="model.price" :enabled="paxEnabled" :value="guestPickerPax" @input="onGuestPickerUpdate"/>
        <h3 class="error" v-if="requiresAccompanyingAdult">{{ $t('form.label.p2p.children-alone') }}</h3>

        <form-input v-if="model.originLocations.length" type="select" :label="$t('form.label.pickup-location')" icon="pickup" :options="model.originLocationsOptions" v-model="uiState.pickup"/>
        <form-input v-if="requiresPickupAddress" type="text" :label="$t('form.label.pickup-address')" icon="pickupaddress"/>
        <form-input v-if="model.destinationLocations.length" type="select" :label="$t('form.label.dropoff-location')" icon="dropoff" :options="model.destinationLocationsOptions" v-model="uiState.dropoff"/>
        <form-input v-if="requiresDropoffAddress" type="text" :label="$t('form.label.dropoff-address')" icon="dropoffaddress"/>

        <!--
        This is an element to tell the user when they have filled out the from and to and there are not pickup/dropoff options. Not sure how to know when to show.
        <p v-if="uiState.from && uiState.to && (!model.originLocationsOptions.length || !model.destinationLocationsOptions.length)">{{ $t('general.label.no-routes') }}</p>
        -->

        <template v-if="requiresAirlineInfo || requiresOutboundAirline">
          <h2 class="airline-title">{{ $t('form.label.p2p.airline-and-flight') }}<span class="optional">{{ $t('general.label.optional') }}</span></h2>

          <template v-if="requiresAirlineInfo">
            <h3>{{ $t('form.label.p2p.inbound-airline') }}</h3>
            <form-input type="select" :label="$t('form.label.p2p.airline')" icon="arrival-airline" :options="airlineOptions"/>
            <div class="airlineInfo-container">
              <form-input type="text" :label="$t('form.label.p2p.flight-num')" icon="arrival-flightinfo"/>
              <form-input type="time" :label="$t('form.label.p2p.flight-time')"/>
            </div>
          </template>

          <template v-if="requiresOutboundAirline">
            <h3>{{ $t('form.label.p2p.outbound-airline') }}</h3>
            <form-input type="select" :label="$t('form.label.p2p.airline')" icon="departing-airline" :options="airlineOptions"/>
            <div class="airlineInfo-container">
              <form-input type="text" :label="$t('form.label.p2p.flight-num')" icon="departing-flightinfo"/>
              <form-input type="time" :label="$t('form.label.p2p.flight-time')"/>
            </div>
          </template>
        </template>

        <hr v-if="model.shouldHaveTransportationRoutes && model.transportationRoutes.length">

        <!-- Step 2 -->
        <div id="step-2" v-if="model.shouldHaveTransportationRoutes && model.transportationRoutes.length && (this.uiState.tripType === 'oneway' || model.transportationRoutesReturn.length)">

          <h2>{{ $t('p2p.step2') }}</h2><br>

          <div class="trip-description" v-if="activeFrom && activeTo && activePickup && activeDropoff">
            <h3>{{ $t('form.label.p2p.departing-trip-description', [activeFrom.name, activeTo.name]) }}</h3>
            <div>{{ activePickup.name }} <span class="arrow">&#10230;</span> {{ activeDropoff.name }}</div>
          </div>

          <button @click="filterOpen = !filterOpen" class="filter-button" :class="{filtered : filterOptions.filtered}">
            <span class="caret-icon"></span>Filter results
          </button>
          <div class="filter-container" v-show="filterOpen">
            <h3>Filter results</h3>
            <hr class="straight">
            <h4>Select Times:</h4>
            <input type="radio" id="departing" v-model="filterOptions.when" value="departing"><label class="radio-label" for="departing">Departing</label>
            <input type="radio" id="arriving" v-model="filterOptions.when" value="arriving"><label class="radio-label" for="arriving">Arriving</label><br>
            <h4>Between:</h4>
            <span class="filter-select"><form-input type="select" v-model="filterOptions.start" :options="timeFilters" label="Starting"></form-input></span>
            &nbsp;&nbsp;&nbsp;and&nbsp;&nbsp;&nbsp;
            <span class="filter-select"><form-input type="select" v-model="filterOptions.end" :options="timeFilters.filter(time => time.value > filterOptions.start)" label="Ending"></form-input></span>
            <div class="button-row">
              <span>
                <button @click="clearFilter" class="clear-filter-button">{{ $t('form.label.p2p.clear') }}</button>
              </span>
              <span>
                <button @click="filterRoutes" class="filter-results-button">{{ $t('form.label.p2p.view-results') }}</button>
              </span>
            </div>
          </div>

          <div class="results-summary">
            {{ resultsString(filteredRoutes.length, activeDate) }}
          </div>

          <div class="transportation-routes">
            <transportation-route v-for="route in filteredRoutes" :route="route" :selected="route.selected" @select="val => selectDeparting(val)" :key="route.id"></transportation-route>
          </div>

          <hr>
        </div>

        <h3 v-if="uiState.from && uiState.to && uiState.pickup && uiState.dropoff && (!model.transportationRoutesReturn.length && uiState.tripType === 'roundtrip' )">{{ $t('form.label.p2p.no-routes-available') }}</h3>

        <!-- Step 3 -->
        <div id="step-3" v-if="uiState.tripType == 'roundtrip' && model.shouldHaveTransportationRoutes && model.transportationRoutesReturn.length && model.transportationRoutes.length">
          <h2>{{ $t('p2p.step3') }}</h2><br>
          <div class="trip-description" v-if="activeFrom && activeTo && activePickup && activeDropoff">
            <h3>{{ $t('form.label.p2p.returning-trip-description', [activeTo.name, activeFrom.name]) }}</h3>
            <div>{{ activeDropoff.name }} <span class="arrow">&#10230;</span> {{ activePickup.name }}</div>
          </div>

          <button @click="filterReturnOpen = !filterReturnOpen" class="filter-button" :class="{filtered : filterReturnOptions.filtered}">
            <span class="caret-icon"></span>Filter results
          </button>
          <div class="filter-container" v-show="filterReturnOpen">
            <h3>Filter results</h3>
            <hr class="straight">
            <h4>Select Times:</h4>
            <input type="radio" id="departing-return" v-model="filterReturnOptions.when" value="departing"><label class="radio-label" for="departing-return">Departing</label>
            <input type="radio" id="arriving-return" v-model="filterReturnOptions.when" value="arriving"><label class="radio-label" for="arriving-return">Arriving</label><br>
            <h4>Between:</h4>
            <span class="filter-select"><form-input type="select" v-model="filterReturnOptions.start" :options="timeFilters" label="Starting"></form-input></span>
            &nbsp;&nbsp;&nbsp;and&nbsp;&nbsp;&nbsp;
            <span class="filter-select"><form-input type="select" v-model="filterReturnOptions.end" :options="timeFilters.filter(time => time.value > filterReturnOptions.start)" label="Ending"></form-input></span>
            <div class="button-row">
              <span>
                <button @click="clearReturnFilter" class="clear-filter-button">{{ $t('form.label.p2p.clear') }}</button>
              </span>
              <span>
                <button @click="filterReturnRoutes" class="filter-results-button">{{ $t('form.label.p2p.view-results') }}</button>
              </span>
            </div>
          </div>

          <div class="results-summary">
            {{ resultsString(filteredReturnRoutes.length, activeReturnDate) }}
          </div>

          <div class="transportation-routes">
            <transportation-route v-for="route in filteredReturnRoutes" :key="route.id" :route="route" :selected="route.selected" @select="val => selectReturning(val)"></transportation-route>
          </div>

          <hr >
        </div>

        <div id="summary" v-show="activeRoute || activeReturnRoute">
          <h2>{{$t('p2p.summary')}}</h2>
          <div v-if="activeRoute">
            <div class="trip-description">
              <h3>{{ $t('form.label.p2p.departing-trip-description', [activeFrom.name, activeTo.name]) }}</h3>
              <div class="route-label">{{ activePickup.name }} <span class="arrow">&#10230;</span> {{ activeDropoff.name }}</div>
              <a @click="removeActiveRoute()" class="remove-button">Remove</a>
            </div>
            <div class="route-summary">
              <div class="left">
                <h3 class="travelDate">{{ dateSummary(activeRoute.departureDate) }}</h3>
                <h2 class="travelTime">{{ activeRoute.departureTime }} - {{ activeRoute.arrivalTime }}</h2>
                <div class="duration"><span class="icon-duration"></span>&nbsp;<duration :hours="travelTime(activeRoute.arrivalTime, activeRoute.departureTime).hours" :minutes="travelTime(activeRoute.arrivalTime, activeRoute.departureTime).minutes"/></div>
                <div class="pax"><span class="icon-passenger"></span> {{ paxString }}</div>
              </div>
              <div class="right">
                <div class="price-summary">{{ $formatter.currency(activeRoute.price) }}</div>
                <div class="fare-type">Standard</div>
              </div>
            </div>
          </div>
          <div v-if="activeReturnRoute">
            <div class="trip-description">
              <h3>{{ $t('form.label.p2p.returning-trip-description', [activeTo.name, activeFrom.name]) }}</h3>
              <div class="route-label">{{ activeDropoff.name }} <span class="arrow">&#10230;</span> {{ activePickup.name }}</div>
              <a @click="removeActiveReturnRoute" class="remove-button">Remove</a>
            </div>
            <div class="route-summary">
              <div class="left">
                <h3 class="travelDate">{{ dateSummary(activeReturnRoute.departureDate) }}</h3>
                <h2 class="travelTime">{{ activeReturnRoute.departureTime }} - {{ activeReturnRoute.arrivalTime }}</h2>
                <div class="duration"><span class="icon-duration"></span>&nbsp;<duration :hours="travelTime(activeReturnRoute.arrivalTime, activeReturnRoute.departureTime).hours" :minutes="travelTime(activeReturnRoute.arrivalTime, activeReturnRoute.departureTime).minutes"/></div>
                <div class="pax"><span class="icon-passenger"></span> {{ paxString }}</div>
              </div>
              <div class="right">
                <div class="price-summary">{{ $formatter.currency(activeReturnRoute.price) }}</div>
                <div class="fare-type">Standard</div>
              </div>
            </div>
          </div>
        </div>

        <hr v-if="totalPrice" class="last">
        <div class="total-price" v-if="totalPrice">{{ $t('general.label.total') }}:<span>{{ $formatter.currency(totalPrice) }}</span></div>

        <button id="checkout-button" class="button-add" @click="onAddToCart" :disabled="isAddToCartDisabled">{{ $t('form.label.add-to-cart') }}</button>

      </div>
      <div class="additional-info">
        <div class="more-info">
          <input id="more-info-toggle" class="toggle-button" type="checkbox">
          <label for="more-info-toggle">{{ $t('general.label.more-info') }}</label>
          <p v-html="model.moreInfo"></p>
        </div>

        <div class="policy">
          <input id="policy-toggle" class="toggle-button" type="checkbox">
          <label for="policy-toggle">{{ $t('general.label.cancel-policy') }}</label>
          <p v-html="model.policies"></p>
        </div>
      </div>
      <linked-products
        v-if="showLinkedProducts && model.relatedProducts && model.relatedProducts.length"
        ref="linkedProducts"
        :items="model.relatedProducts"
        :nextRoute="{name: 'cart'}">
      </linked-products>
  </div>
</template>

<script>
import moment from 'moment';
import PortalSettings from '@/models/PortalSettings';
import GuestPicker from '@/components/GuestPicker.vue'
import FormInput from '@/components/FormInput.vue';
import TransportationRoute from '@/components/TransportationRoute.vue';
import LinkedProducts from '@/components/LinkedProducts.vue';
import P2P from '@/models/P2P';
import Category from '@/models/Category';
import CartItem from '@/models/CartItem';
import Cart from '@/models/Cart';

import PAP from '@/mixins/pap';
import dateItem from '@/mixins/dateItem';
import activityItem from '@/mixins/activityItem';

import Duration from '@/components/Duration.vue';
import Datepicker from 'vuejs-datepicker';

// NOTE there is some seemingly redundant state objects, please see the notes about each one.
export default {
  name: 'PointToPoint',
  mixins: [ PAP, activityItem ],

  // state coming in on page load. This comes from the router, which are query URL params
  props: ['date', 'returnDate', 'fromCityId', 'toCityId', 'roundTrip', 'pickupId', 'dropoffId', 'showLinkedProducts', 'selectedRouteId', 'selectedReturnRouteId'],

  data() {
    return {
      id: 'p2p-noid',
      type: 'activity', // for mixins
      routeName: 'p2p', // override for dateItem mixin
      bookNowActive: false,
      paxPrefix: '',
      loading: false,
      cartItem: new CartItem(),
      firstRefresh: true,
      filterOpen: false,
      filterReturnOpen: false,
      filterOptions:{
        filtered: false,
        when: 'departing',
        start: 0,
        end: 0
      },
      filterReturnOptions:{
        filtered: false,
        when: 'departing',
        start: 0,
        end: 0
      },

      timeFilters: [
      {label: "00:00", value: 0},
      {label: "01:00", value: 1},
      {label: "02:00", value: 2},
      {label: "03:00", value: 3},
      {label: "04:00", value: 4},
      {label: "05:00", value: 5},
      {label: "06:00", value: 6},
      {label: "07:00", value: 7},
      {label: "08:00", value: 8},
      {label: "09:00", value: 9},
      {label: "10:00", value: 10},
      {label: "11:00", value: 11},
      {label: "12:00", value: 12},
      {label: "13:00", value: 13},
      {label: "14:00", value: 14},
      {label: "15:00", value: 15},
      {label: "16:00", value: 16},
      {label: "17:00", value: 17},
      {label: "18:00", value: 18},
      {label: "19:00", value: 19},
      {label: "20:00", value: 20},
      {label: "21:00", value: 21},
      {label: "22:00", value: 22},
      {label: "23:00", value: 23},
      ],

      // state that is actively modified by the UI. Defaults to what came from props
      uiState: {
        tripType: this.roundTrip ? 'roundtrip' : 'oneway',
        from: this.fromCityId,
        to: this.toCityId,
        pickup: this.pickupId,
        dropoff: this.dropoffId,
        pax: {},
        returnDate: this.returnDate ? moment(this.returnDate).toDate() : this.$store.state.activeDate,
        selectedRouteId: this.selectedRouteId,
        selectedReturnRouteId: this.selectedReturnRouteId
      },

      airlines: false,

      disabledDates: {
        to: moment().startOf('day').toDate()
      },

      activeRouteId: 0,
      activeReturnRouteId: 0
    }
  },
  mounted() {
    //To insert label in vuejs-datepicker (third-party component)
    if(!document.getElementById('p2pDateDepartureLabel')){
      let p2pDateDepartureLabel = document.createElement('label');
      p2pDateDepartureLabel.setAttribute('id','p2pDateDepartureLabel');
      p2pDateDepartureLabel.innerText ='Departure';
      document.getElementById('p2pDateDeparture').parentElement.appendChild(p2pDateDepartureLabel);
    }
    if(!document.getElementById('p2pDateReturnLabel')){
      let p2pDateReturnLabel = document.createElement('label');
      p2pDateReturnLabel.setAttribute('id','p2pDateReturnLabel');
      p2pDateReturnLabel.innerText ='Return';
      document.getElementById('p2pDateReturn').parentElement.appendChild(p2pDateReturnLabel);
    }
  },
  created() {
    if (this.date) {
      // TODO check date that came from URL (prop) is >= today
      this.$store.dispatch('setActiveDate', moment(this.date).toDate());
    } else {
      this.$store.dispatch('setActiveDate', moment(new Date()).add(1,'days').toDate());
    }

    if (!this.returnDate || !moment(this.returnDate).isAfter(moment(this.activeDate)) ){
      this.uiState.returnDate = moment(this.activeDate).add(7,'days').toDate();
    }

    this.refreshModel();
  },

  computed: {
    paxString(){
      var string = '';
      for (var type in this.uiState.pax){
        if (this.uiState.pax[type])
          string += `${this.uiState.pax[type]} ${type}, `;
      }
      return string.substring(0, string.length-2);
    },
    model() {
      return P2P.find(this.id) || new P2P();
    },

    filteredRoutes() {
      if (!this.filterOptions.filtered){
        return this.model.transportationRoutes;
      }
      return this.model.transportationRoutes.filter((route) => {
        if (this.filterOptions.when === "departing"){
          return (route.rawDepartureTime.hour >= this.filterOptions.start && route.rawDepartureTime.hour < this.filterOptions.end);
        } else {
          return (route.rawArrivalTime.hour >= this.filterOptions.start && route.rawArrivalTime.hour < this.filterOptions.end);
        }
      });
    },

    filteredReturnRoutes() {
      if (!this.filterReturnOptions.filtered){
        return this.model.transportationRoutesReturn;
      }
      return this.model.transportationRoutesReturn.filter((route) => {
        if (this.filterReturnOptions.when === "departing"){
          return (route.rawDepartureTime.hour >= this.filterReturnOptions.start && route.rawDepartureTime.hour < this.filterReturnOptions.end);
        } else {
          return (route.rawArrivalTime.hour >= this.filterReturnOptions.start && route.rawArrivalTime.hour < this.filterReturnOptions.end);
        }
      });
    },

    activeDate() {
      return this.$store.state.activeDate;
    },

    activeReturnDate() {
      return this.uiState.returnDate;
    },

    isRoundTrip() {
      return this.uiState.tripType === 'roundtrip';
    },

    pickupLocations() {
      return [];
    },

    dropoffLocations() {
      return [];
    },

    requiresPickupAddress() {
      let active = this.activePickup;
      return active && active.requireSpecificAddress;
    },

    requiresDropoffAddress() {
      let active = this.activeDropoff;
      return active && active.requireSpecificAddress;
    },

    requiresAirlineInfo() {
      let active = this.activePickup;
      return active && active.isAirport;
    },

    requiresOutboundAirline() {
      let active = this.activeDropoff;
      return this.isRoundTrip && active && active.isAirport;
    },

    requiresAccompanyingAdult() {
      let requiresAdult = false;
      let areInfantsResponsiblePeople = PortalSettings.get('infantsAreResponsiblePeople');

      let adults = this.uiState.pax.adults ? this.uiState.pax.adults : 0;
      let seniors = this.uiState.pax.seniors ? this.uiState.pax.seniors : 0;
      let students = this.uiState.pax.students ? this.uiState.pax.students : 0;
      let children = this.uiState.pax.children ? this.uiState.pax.children : 0;
      let infants = this.uiState.pax.infants ? this.uiState.pax.infants : 0;
      let responsiblePax = adults + seniors + students;

      if(PortalSettings.get('p2pAvailabilityShowErrorBookingChildrenOnly')) {
        //Are there any adults?
        if(areInfantsResponsiblePeople)
           responsiblePax += infants; //Airporter uk uses infants as adults or something

        //Does this have kids?
        let hasKids = (children > 0 || (!areInfantsResponsiblePeople && infants > 0));

        //If only children or infants are selected and there are no responsible people...
        if(hasKids && responsiblePax == 0) {
          requiresAdult = true;
        }
      }

      return requiresAdult;
    },

    airlineOptions() {
      // lazyloading hack? TODO jamie don't really like this...
      let airlines = this.airlines;

      if (airlines === false) {
        airlines = [];
        this.fetchAirlines(); // this is async
      }

      return P2P.mapToOptions(airlines);
    },

    activeFrom() {
      return this.findActive(this.model.origins, this.uiState.from);
    },

    activeTo() {
      return this.findActive(this.model.destinations, this.uiState.to);
    },

    activePickup() {
      return this.findActive(this.model.originLocations, this.uiState.pickup);
    },

    activeDropoff() {
      return this.findActive(this.model.destinationLocations, this.uiState.dropoff);
    },

    disabledReturnDates() {
      return { to: this.activeDate };
    },

    isAddToCartDisabled() {
      return !this.activeRouteId || (this.isRoundTrip && !this.activeReturnRouteId) || this.requiresAccompanyingAdult;
    },

    activeRoute() {
      return this.findActive(this.model.transportationRoutes, this.activeRouteId);
    },

    activeReturnRoute() {
      return this.findActive(this.model.transportationRoutesReturn, this.activeReturnRouteId);
    },

    totalPrice() {
      let price = 0;

      if (this.uiState.tripType === "roundtrip" && !this.activeReturnRoute){
        return price;
      }

      if (this.activeRoute) {
        price = this.activeRoute.price;
      }

      if (this.activeRoute && this.activeReturnRoute) {
        price += this.activeReturnRoute.price;
      }


      return price;
    },

    relatedProducts() {
      let relatedProducts = [];

      if(this.activeRoute) {
        relatedProducts = relatedProducts.concat(this.activeRoute.relatedProducts);
      }

      if(this.activeReturnRoute) {
        relatedProducts = relatedProducts.concat(this.activeReturnRoute.relatedProducts);
      }

      return relatedProducts;
    }
  },

  watch: {
    uiState: {
      handler() {
        this.refreshModel();
        this.selectDeparting(0);
        this.selectReturning(0);
      },
      deep: true
    }
  },

  methods: {
    filterRoutes(){
      this.filterOptions.filtered = true;
      this.filterOpen = false;
    },
    clearFilter(){
      this.filterOpen = false;
      this.filterOptions.filtered = false;
    },
    filterReturnRoutes(){
      this.filterReturnOpen = false;
      this.filterReturnOptions.filtered = true;
    },
    clearReturnFilter(){
      this.filterReturnOpen = false;
      this.filterReturnOptions.filtered = false;
    },
    resultsString(results, date){
      return this.$tc('p2p.results', results, {0: moment(date).format("ddd, MMM Do YYYY")});
    },
    findActive(arr, id) {
      let active = arr.filter(item => item.id === id);
      return active && active.length ? active[0] : null;
    },

    dateSummary(date){
      return moment(date).format("MMM D, YYYY");
    },

    selectDeparting(id){
      if (id === 0){
        this.activeRouteId = 0;
      } else {
        this.model.transportationRoutes.forEach((route) => {
          if (route.id === id){
            if (!route.selected){
              this.activeRouteId = id;
              route.selected = true;
              if (this.uiState.tripType === "roundtrip" && !this.activeReturnRouteId){
                this.$el.querySelector('#step-3').scrollIntoView({behavior: "smooth"});
              } else {
                window.scrollTo({top: document.body.scrollHeight, left: 0, behavior: "smooth"});
              }
            }
            else {
              this.activeRouteId = 0;
              route.selected = false;
            }
          } else {
            route.selected = false;
          }
        });
      }
      this.$forceUpdate()
    },

    selectReturning(id){
      if (id === 0){
        this.activeReturnRouteId = 0;
      } else {
        this.model.transportationRoutesReturn.forEach((route) => {
          if (route.id === id){
            if (!route.selected){
              this.activeReturnRouteId = id;
              route.selected = true;
              if (this.activeRouteId){
                window.scrollTo({top: document.body.scrollHeight, left: 0, behavior: "smooth"});
              } else {
                this.$el.querySelector('#step-2').scrollIntoView({behavior: "smooth"});
              }
            }
            else {
              this.activeReturnRouteId = 0;
              route.selected = false;
            }
          } else {
            route.selected = false;
          }
        });
      }
      this.$forceUpdate()
    },

    removeActiveRoute(){
      this.model.transportationRoutes.forEach((route) => {
        route.selected = false;
      });
      this.activeRouteId = 0;
    },

    removeActiveReturnRoute(){
      this.model.transportationRoutesReturn.forEach((route) => {
        route.selected = false;
      });
      this.activeReturnRouteId = 0;
    },

    travelTime(arrival, departure) {
      let b = moment(arrival, 'h:mm A');
      let a = moment(departure, 'h:mm A');
      let diff = b.diff(a, 'minutes');

      if (diff < 0) {
        // then it's next day.
        b = b.add(1, 'day');
        diff = b.diff(a, 'minutes');
      }

      var time = {hours: 0, minutes: 0};
      time.hours = Math.floor(diff / 60);
      time.minutes = diff - (60 * time.hours);
      return time;
    },

    refreshModel(hard = false) {
      this.loading = true;
      let p2pState = this.getP2PState();

      // we want to update the query params so if user refreshes, the state is more or less maintained.
      this.$router.replace({ name: this.$router.name, query: p2pState });

      return P2P.fetch(p2pState).then(() => {
        this.resetItem(this.firstRefresh);
        this.loading = false;
        if (this.firstRefresh) {
          this.firstRefresh = false;
          // Category.fetch(this.type, this.model.category); /// no longer need category for P2P
        }
      });
    },

    getP2PState() {
      let ui = this.uiState;
      let state = {
        roundTrip: this.isRoundTrip ? 1 : 0,
        date: moment(this.activeDate).format('YYYY-MM-DD')
      };

      if (ui.from) {
        state.from = ui.from;

        if (ui.to) {
          state.to = ui.to;

          if (ui.pickup) {
            state.pickup = ui.pickup;
          }

          if (ui.dropoff) {
            state.dropoff = ui.dropoff;
          }

          if (state.pickup && state.dropoff && ui.pax) {
            state = { ...state, ...ui.pax };
          }

          if (state.roundTrip && ui.returnDate) {
            state.returnDate = moment(ui.returnDate).format('YYYY-MM-DD');
          }
        }
      }

      if(ui.selectedRouteId) {
        state.selectedRouteId = ui.selectedRouteId
      }

      if(ui.selectedReturnRouteId) {
        state.selectedReturnRouteId = ui.selectedReturnRouteId
      }

      return state;
    },

    fetchAirlines() {
      P2P.fetchAirlines().then(a => this.airlines = a);
    },

    resetItem(hard = false) {
      let defaults = { itemId: this.id, type: 'p2p', pax: this.cartItem.pax };

      if (hard) {
        // model has updated so set the prefix so guest-picker uses correct lang overrides.
        this.paxPrefix = `activity${this.id}.`;
        this.$refs.guestPicker.reset();
        this.uiState.pax = this.guestPickerPax;
      }

      // do this last
      this.cartItem = new CartItem(defaults);
    },

    onGuestPickerUpdate(val) {
      this.uiState.pax = val;
    },

    updateActiveDate(val) {
      this.$store.dispatch('setActiveDate', val);

      // make sure the return date is after the departure date.
      if (val > this.uiState.returnDate) {
        this.uiState.returnDate = val;
      }
      this.refreshModel();
    },

    updateReturnDate(val) {
      this.uiState.returnDate = val;
      this.refreshModel();
    },

    setActiveRoute(id) {
      this.activeRouteId = id;
    },

    setActiveReturnRoute(id) {
      this.activeReturnRouteId = id;
    },

    onAddToCart() {
      var route = {name: 'cart'};
      if (this.relatedProducts && this.relatedProducts.length){
        let query = this.$route.query;

        query.selectedRouteId = this.activeRouteId;
        query.selectedReturnRouteId = this.activeReturnRouteId;

        route = {name: "p2p-linked-products", query: query, params: this.$route.params};
      }
      this.loading = true;
      var firstItem = new CartItem(
        {
          itemId: this.activeRoute.id,
          type: "activity",
          date: moment(this.activeRoute.departureDate).format('YYYY-MM-DD'),
          startTime: this.activeRoute.rawDepartureTime.hour + ":" + this.activeRoute.rawDepartureTime.minute,
          pickupLocation: this.activePickup.id,
          dropoffLocation: this.activeDropoff.id,
          pax: this.uiState.pax
        });
      var secondItem = {};
      if (this.activeReturnRoute){
        secondItem = new CartItem(
        {
          itemId: this.activeReturnRoute.id,
          type: "activity",
          date: moment(this.activeReturnRoute.departureDate).format('YYYY-MM-DD'),
          startTime: this.activeReturnRoute.rawDepartureTime.hour + ":" + this.activeReturnRoute.rawDepartureTime.minute,
          pickupLocation: this.activeDropoff.id,
          dropoffLocation: this.activePickup.id,
          pax: this.uiState.pax
        });
      }
      Cart.add(firstItem).then(() => {
        if (this.activeReturnRoute){
          Cart.add(secondItem).then(() => {
            this.loading = false;
            this.$router.push(route);
          });
        }
        else {
          this.loading = false;
          this.$router.push(route);
        }
      });
    }
  },

  components: {
    GuestPicker,
    FormInput,
    Datepicker,
    TransportationRoute,
    Duration,
    LinkedProducts
  }
};
</script>

<style lang="scss">
.view.p2p {
  .picker-modal {
    > .content-wrapper {
      @include breakpoint(medium) {
        left: 10px !important;
      }
    }
  }
  h1 {
    @include breakpoint(medium) {
      margin-bottom: 40px;
    }
  }
  .time-title, .airline-title {
    padding-top: 20px;
  }

  // Page layout
  @include breakpoint(medium) {

    .container {
      @include clearfix;

      .description {
        float: left;
      }
      .form {
        float: left;
        width: calc(100% - 330px);
        margin-right: 30px;
      }
      .additional-info {
        float: left;
        width: 100%;
      }
    }
  }

  .description {
    margin-bottom: 30px;
  }

  // Type
  .type-container {
    display: flex;

    .component.form-input .input {
      width: auto;

      input[type="radio"] {

        &+label, &+.label {
          font-size: 14px;
          line-height: 40px;
          padding: 0 12px;
          color: #fff;
          background-color: #bbb;
          position: static;

          &::before {
            content: none;
          }
        }

        &:checked + label, &:checked + .label {
          background-color: $colorCTA;
        }
      }
    }

    .oneway label {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
    .round label {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }

  .date-container,
  .destination-container,
  .airlineInfo-container {
    @include inputs-in-1row;
  }

  // form override style
  .component.form-input {
    @include form-layout-wide;
  }

  .type .icon { @include font-awesome($fa-var-map-signs, light); line-height: 40px !important; }
  .date .icon { @include font-awesome($fa-var-calendar-alt, light); }
  .destination .icon { @include font-awesome($fa-var-route, light); }
  .pickup .icon { @include font-awesome($fa-var-map-marker-alt, light); }
  .pickupaddress .icon { @include font-awesome($fa-var-map-marker-edit, light); }
  .dropoff .icon { @include font-awesome($fa-var-map-marker-alt, light); }
  .dropoffaddress .icon { @include font-awesome($fa-var-map-marker-edit, light); }
  .departing-airline .icon { @include font-awesome($fa-var-plane-departure, light); }
  .departing-flightinfo .icon { @include font-awesome($fa-var-clock, light); }
  .arrival-airline .icon { @include font-awesome($fa-var-plane-arrival, light); }
  .arrival-flightinfo .icon { @include font-awesome($fa-var-clock, light); }


  // Time section
  .selected-info {
    background-color: $colorBgGray;
    padding: 5px 15px;
    margin-bottom: 10px;

    .icon {
      display: flex;
      align-items: center;
      margin: 5px 0;

      &::before {
        color: $colorGray;
        font-size: 18px;
        width: 20px;
        text-align: center;
        margin-right: 10px;
      }
    }

    .selected-date::before { @include font-awesome-pseudo($fa-var-calendar-alt, light); }
    .selected-destination::before { @include font-awesome-pseudo($fa-var-route, light); }
    .selected-to::before {
      @include font-awesome-pseudo($fa-var-long-arrow-alt-right);
      color: $colorGray;
      margin: 0 5px;
    }

    .edit-date {
      margin-left: 6px;
    }
  }

  .time-input {
    padding: 8px 15px;
    &:last-of-type {
      margin-bottom: 25px;
    }
    @include breakpoint(medium) {
      padding: 8px 40px;
    }

    .transportation-route {
      @include font-input;
      display: grid;
      grid-template-columns: auto 1fr;
      align-items: center;

      & > div {
        display: grid;
        display: -ms-grid;
        grid-template:
            "time price"
            "info info";

        .time {
          grid-area: time;
          -ms-grid-row: 1;
          -ms-grid-column: 1;
          font-weight: 500;
          @include breakpoint(medium) {
            font-size: 15px;
          }
        }
        .price {
          grid-area: price;
          -ms-grid-row: 1;
          -ms-grid-column: 2;
          text-align: right;
          font-weight: 500;
          @include breakpoint(medium) {
            font-size: 15px;
          }
          .price-original {
            color: $colorGray;
            text-decoration: line-through;
          }
          .price-discounted {
            color: $colorRed;
            margin-left: 6px;
          }
        }
        .info {
          @include font-small;
          color: $colorGray;
          grid-area: info;
          -ms-grid-row: 2;
          -ms-grid-column: 1;

          .remaining {
            margin-left: 10px;
          }
        }
      }
    }
  }
  // END Time section

  .optional {
    @include font-small;
    font-weight: normal;
    color: $colorGray;
    margin-left: 10px;
  }

  .total-price {
    font-size: 24px;
    font-weight: 500;
    text-align: right;
    padding-top: 30px;
    margin-bottom: 30px;
    &::before {
      @include breakpoint(medium) {
        margin: 0 0 20px 0;
        width: 100%;
      }
    }

    span {
      margin-left: 10px;
    }
  }

  .button-add {
    @include button($colorCTA, #fff);
    width: 30%;
    min-width: 240px;
    margin-bottom: 50px;
    float: right;

    &:disabled {
      background-color: #ddd;
      pointer-events: none;
    }
  }

  .transportation-routes > * {
    margin: 20px 0;
  }

  .route-summary {
    overflow: auto;
    background-color: $colorBgGray;
    padding: 12px;
    padding-left: 20px;
    padding-right: 20px;
    display: flex;
    align-items: center;
    border: 2px solid $colorPaleGray;

    > .left {
      width: 50%;
      display: inline-block;
      > * {
        padding-bottom: 3px;
        padding-top: 3px;
      }
    }

    > .right {
      display: inline-block;
      text-align: right;
      width: 50%;
      height: 100%;
      > .fare-type {
        padding-top: 10px;
        color: $colorGray;
        font-size: 16px;
        margin-right: 14px;
      }
    }
  }

  h2 {
    font-size: 21px;
  }

  h4 {
    font-weight: 400;
  }

  .travelDate {
    font-size: 15px;
    font-weight: 400;
    margin-bottom: 0;
  }

  .results-summary {
    text-align: center;
    color: $colorGray;
  }

  .price-summary {
    font-size: 21px;
    font-weight: 500;
    margin: auto;
    top: 50%;
  }

  #summary {
    > * {
      padding-bottom: 20px;
    }
  }

  .trip-description {
    padding-bottom: 20px;
  }

  .remove-button {
    color: $colorCTA;
    display: inline-block;
    float: right;
    margin-top: 2px;
  }

  .route-label {
    width: 80%;
    display: inline-block;
  }

  hr {
    margin: 45px 0 45px 0;
    border-top: 1px solid $colorPaleGray;
    text-align: center;
    &::after {
      background: #fff;
      @include font-awesome-pseudo($fa-var-chevron-down, light);
      color: $colorGray;
      position: relative;
      top: -7px;
    }
    &.last {
      margin-bottom: 0;
      margin-top: 25px;
    }
    &.straight {
      &::after {
        display: none;
      }
      margin: 0;
    }
  }

  #step-2 {
    padding-top: 75px;
    margin-top: -75px;
  }

  #step-3 {
    padding-top: 75px;
    margin-top: -75px;
  }

  .arrow {
    font-size: 1.3em;
    color: $colorOrange;
    padding-left: 4px;
    padding-right: 4px;
  }

  .filter-button {
    border: 1px solid $colorPaleGray;
    padding: 8px;
    margin-bottom: 10px;
    width: intrinsic;
    width: -moz-max-content;
    width: -webkit-max-content;
    width: max-content;
  }

  .caret-icon {
    @include font-awesome($fa-var-angle-double-down);
    padding-right: 10px;
  }

  .filter-container {
    min-width: 300px;
    max-width: 90%;
    border: 1px solid $colorPaleGray;
    position: absolute;
    background: #fff;
    z-index: 999;
    padding: 15px;
  }

  .radio-label {
    padding-right: 25px;
  }

  .filter-results-button {
    @include button($colorCTA, #fff);
  }

  .clear-filter-button {
    @include button($colorPaleGray, #fff);
  }

  .filtered {
    border: 2px solid $colorYellow;
  }

  .button-row {
    > span {
      display: inline-block;
      text-align: center;
      width: 60%;
      &:first-child {
        width: 40%;
      }
    }
  }

  .filter-select {
    display: inline-block;
    width: 40%;
  }

  .more-info {
    @include toggle-info;
    border-bottom: none;
    margin-top: 40px;
  }

  .policy {
    @include toggle-info;
  }

  .date-repicker {
    position: relative;
    input {
      position: absolute;
      margin-left: 5px;
      opacity: 0;
      width: 100%;
      height: auto;
      cursor: pointer;
    }

    label.edit-date {
      @include anchor;
    }
  }

  .error {
    color: $colorRed;
    margin: 20px;
  }
}
</style>